import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchReferralForm } from "../../../store/slices/referralFormsSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function ReferralFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const referralState = useSelector((state) => state.referralForms || {});
    const { currentReferralForm, isLoading, error } = referralState;

    useEffect(() => {
        if (id) dispatch(fetchReferralForm(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentReferralForm) {
            const timer = setTimeout(() => window.print(), 800);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentReferralForm, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading Referral Report...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentReferralForm) return <div className="p-8 text-center">No Referral record found.</div>;

    const d = currentReferralForm;

    // Helper to format dates professionally
    const fmtDate = (dateStr) =>
        dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        }) : "";

    const fmtDateTime = (dtStr) =>
        dtStr ? new Date(dtStr).toLocaleString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: true
        }) : "";

    const managementOptions = [
        "Specialist Opinion",
        "Admission",
        "Investigation",
        "Other"
    ];

    return (
        <>
            <style jsx global>{`
                * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; }
                .print-container { padding: 40px !important; position: relative !important; }
                .logo { position: absolute !important; top: 20px !important; left: 20px !important; }
                .logo img { height: 75px !important; width: auto !important; }
                .header { text-align: center !important; margin-bottom: 35px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.7rem !important; font-weight: bold !important; margin-bottom: 5px !important; }
                .header h6 { font-size: 1.1rem !important; text-transform: uppercase !important; letter-spacing: 1px !important; }
                
                .section-title { background: #f2f2f2 !important; padding: 6px 12px !important; font-weight: bold !important; margin: 25px 0 12px 0 !important; border: 1px solid #000 !important; display: block !important; font-size: 0.95rem !important; }
                .info-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 20px !important; margin-bottom: 15px !important; }
                .info-item { font-size: 1rem !important; line-height: 1.5 !important; }
                .underline { border-bottom: 1px solid #000 !important; font-weight: bold !important; padding: 0 5px !important; display: inline-block !important; min-width: 160px !important; }
                
                .checkbox-row { display: flex !important; align-items: center !important; margin-bottom: 8px !important; }
                .checkbox-box { width: 18px !important; height: 18px !important; border: 1px solid #000 !important; margin-right: 12px !important; text-align: center !important; line-height: 16px !important; font-weight: bold !important; font-size: 14px !important; }
                .ticked { background-color: #000 !important; color: #fff !important; }
                
                .text-box { border: 1px solid #000 !important; padding: 12px !important; min-height: 90px !important; margin-top: 5px !important; line-height: 1.6 !important; font-size: 0.95rem !important; white-space: pre-wrap !important; }
                
                .acknowledgment-box { border: 2px dashed #000 !important; padding: 15px !important; margin-top: 30px !important; }
                .footer { margin-top: 60px !important; border-top: 1px solid #000 !important; padding-top: 12px !important; text-align: center !important; font-size: 0.85rem !important; }
                
                .signature-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 50px !important; margin-top: 40px !important; }
                .sig-line { border-top: 1px solid #000 !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; width: 80% !important; margin: 0 auto !important; }
            `}</style>

            <div className="print-container">
                <div className="logo"><img src={logo} alt="TRH Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>PATIENT REFERRAL FORM</h6>
                </div>

                {/* General Details */}
                <div className="info-grid" style={{ marginTop: '20px' }}>
                    <div className="info-item">Date: <span className="underline">{fmtDate(d.date)}</span></div>
                    <div className="info-item">Referring Facility: <span className="underline">{d.referring_facility || d.referringFacility}</span></div>
                </div>

                {/* Patient Details */}
                <span className="section-title">PATIENT DETAILS</span>
                <div className="info-grid">
                    <div className="info-item">Patient Name: <span className="underline">{d.patient_name || d.patientName}</span></div>
                    <div className="info-item">DOB: <span className="underline">{fmtDate(d.dob)}</span></div>
                    <div className="info-item">Gender: <span className="underline">{d.gender}</span></div>
                    <div className="info-item">Contact No: <span className="underline">{d.contact}</span></div>
                </div>

                {/* Medical Details */}
                <span className="section-title">MEDICAL INFORMATION</span>
                <div className="info-item" style={{ marginBottom: '15px' }}>
                    <strong>Primary Complaint / Diagnosis:</strong>
                    <div className="text-box">{d.primary_complaint_diagnosis || d.primaryComplaintDiagnosis}</div>
                </div>
                <div className="info-item" style={{ marginBottom: '15px' }}>
                    <strong>Current Medication:</strong>
                    <div className="text-box">{d.current_medication || d.currentMedication}</div>
                </div>
                <div className="info-item">
                    <strong>History / Previous Treatment:</strong>
                    <div className="text-box">{d.history_previous_treatment || d.historyPreviousTreatment}</div>
                </div>

                {/* Referral Information */}
                <span className="section-title">REFERRAL & FURTHER MANAGEMENT</span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
                    {managementOptions.map((opt) => {
                        const isSelected = d.further_management === opt || d.furtherManagement === opt;
                        return (
                            <div key={opt} className="checkbox-row">
                                <span className={`checkbox-box ${isSelected ? "ticked" : ""}`}>{isSelected ? "✓" : ""}</span>
                                <span style={{ fontSize: '0.9rem' }}>{opt === "Other" && (d.other_management || d.otherManagement) ? `Other: ${d.other_management || d.otherManagement}` : opt}</span>
                            </div>
                        );
                    })}
                </div>

                <div className="info-grid">
                    <div className="info-item">Referred To (Facility): <span className="underline">{d.referred_to || d.referredTo}</span></div>
                    <div className="info-item">Dept / Consultant: <span className="underline">{d.department_consultant || d.departmentConsultant}</span></div>
                </div>

                {/* Acknowledgment */}
                <div className="acknowledgment-box">
                    <div className="checkbox-row" style={{ marginBottom: '15px' }}>
                        <span className={`checkbox-box ${d.acknowledgment_accepted || d.acknowledgmentAccepted ? "ticked" : ""}`}>
                            {(d.acknowledgment_accepted || d.acknowledgmentAccepted) ? "✓" : ""}
                        </span>
                        <strong>I acknowledge and accept the referral.</strong>
                    </div>
                    <div className="info-grid" style={{ gridTemplateColumns: '1.5fr 1fr 1.5fr' }}>
                        <div className="info-item">Name: <span className="underline">{d.acknowledgment_name || d.acknowledgmentName}</span></div>
                        <div className="info-item">Relation: <span className="underline">{d.acknowledgment_relation || d.acknowledgmentRelation}</span></div>
                        <div className="info-item">Date & Time: <span className="underline">{fmtDateTime(d.acknowledgment_date_time || d.acknowledgmentDateTime)}</span></div>
                    </div>
                </div>

                {/* Signatures */}
                <div className="signature-grid">
                    <div>
                        <div style={{ height: '60px' }}></div>
                        <div className="sig-line">Referring Officer Signature</div>
                    </div>
                    <div>
                        <div style={{ height: '60px' }}></div>
                        <div className="sig-line">Patient / Guardian Signature</div>
                    </div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
        </>
    );
}